import { FormattedMessage } from 'react-intl';
import { CaseClosingCheckDto, UnfinishedCaseTaskDto } from 'types/caseClosing';
import { EMPTY_VALUE, SummaryDate } from './CaseSummarySection';
import { taskUrl } from './closingLinks';
import { CollapsibleLine, PlainLine } from './CollapsibleLine';
import { NewTabLink } from './WarningRow';

export function UnfinishedTasksSection({ check }: { check: CaseClosingCheckDto }) {
    const { isBlocking, totalCount, notAccessibleCount, accessibleTasks } = check.unfinishedTasks;

    if (totalCount === 0) {
        return (
            <PlainLine>
                <FormattedMessage id="case-closing-modal.tasks.none" />
            </PlainLine>
        );
    }

    return (
        <CollapsibleLine isWarning={isBlocking} summary={<TasksSummary check={check} />}>
            {isBlocking ? null : (
                <p className="m-0">
                    <FormattedMessage id="case-closing-modal.tasks.for-information" />
                </p>
            )}
            {accessibleTasks.length > 0 ? (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {accessibleTasks.map((task) => (
                        <TaskRow key={task.id} task={task} />
                    ))}
                </ul>
            ) : null}
            {notAccessibleCount > 0 ? (
                <p className="m-0">
                    <FormattedMessage
                        id={
                            isBlocking
                                ? 'case-closing-modal.tasks.no-access-blocking'
                                : 'case-closing-modal.tasks.no-access-not-blocking'
                        }
                        values={{
                            count: notAccessibleCount,
                            name: check.responsibleLawyer?.name ?? EMPTY_VALUE,
                        }}
                    />
                </p>
            ) : null}
        </CollapsibleLine>
    );
}

function TasksSummary({ check }: { check: CaseClosingCheckDto }) {
    const { isBlocking, totalCount, notAccessibleCount } = check.unfinishedTasks;
    if (isBlocking) {
        return <FormattedMessage id="case-closing-modal.tasks.blocking" values={{ count: totalCount }} />;
    }
    return notAccessibleCount > 0 ? (
        <FormattedMessage
            id="case-closing-modal.tasks.not-blocking-with-no-access"
            values={{ count: totalCount, noAccessCount: notAccessibleCount }}
        />
    ) : (
        <FormattedMessage id="case-closing-modal.tasks.not-blocking" values={{ count: totalCount }} />
    );
}

function TaskRow({ task }: { task: UnfinishedCaseTaskDto }) {
    const responsible = task.assignedToEmployees.map((employee) => employee.name).join(', ') || EMPTY_VALUE;
    return (
        <li className="flex flex-col gap-0.5">
            <span className="font-semibold">{task.name}</span>
            <span className="text-fg-secondary">
                {responsible} · <SummaryDate value={task.deadlineDate} />
            </span>
            <NewTabLink href={taskUrl(task.id)}>
                <FormattedMessage id="case-closing-modal.tasks.open-task" />
            </NewTabLink>
        </li>
    );
}
